import React, { useState, useEffect } from "react";

interface Transaction {
    id: string;
    reference: string;
    amount: number;
    currency: string;
    type: 'credit' | 'debit';
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    description: string;
    timestamp: string;
    gate?: string;
    fees?: number;
    balance_after?: number;
}

interface BillingTransactionsProps {
    userEmail: string;
}

const BillingTransactions: React.FC<BillingTransactionsProps> = ({ userEmail }) => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        fetchTransactions();
    }, [userEmail]);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://64.227.174.0:8004'}/billing/api/transactions/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: userEmail
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setTransactions(data.data.transactions || []);
            } else {
                // Fallback to mock data
                setTransactions(getMockTransactions());
            }
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
            setTransactions(getMockTransactions());
        } finally {
            setLoading(false);
        }
    };

    const getMockTransactions = (): Transaction[] => [
        {
            id: '1',
            reference: 'TXN-2024-001',
            amount: 15000,
            currency: 'KES',
            type: 'credit',
            status: 'completed',
            description: 'Payment received from Patient A',
            timestamp: '2024-01-15T10:30:00Z',
            gate: 'mpesa',
            fees: 50,
            balance_after: 15000
        },
        {
            id: '2',
            reference: 'TXN-2024-002',
            amount: 5000,
            currency: 'KES',
            type: 'debit',
            status: 'completed',
            description: 'Service fee payment',
            timestamp: '2024-01-14T15:45:00Z',
            gate: 'card',
            fees: 25,
            balance_after: 10000
        },
        {
            id: '3',
            reference: 'TXN-2024-003',
            amount: 8000,
            currency: 'KES',
            type: 'credit',
            status: 'pending',
            description: 'Consultation fee',
            timestamp: '2024-01-13T09:15:00Z',
            gate: 'airtel',
            fees: 40,
            balance_after: 18000
        }
    ];

    const formatCurrency = (amount: number, currency: string = 'KES') => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'danger';
            case 'cancelled': return 'secondary';
            default: return 'info';
        }
    };

    const getTypeIcon = (type: string) => {
        return type === 'credit' ? 'ri-arrow-up-line text-green-600' : 'ri-arrow-down-line text-red-600';
    };

    const filteredTransactions = transactions.filter(transaction => {
        const matchesFilter = filter === 'all' || transaction.status === filter;
        const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             transaction.reference.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    const sortedTransactions = [...filteredTransactions].sort((a, b) => {
        let aValue: any = a[sortBy as keyof Transaction];
        let bValue: any = b[sortBy as keyof Transaction];

        if (sortBy === 'timestamp') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
        }

        if (sortOrder === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTransactions = sortedTransactions.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const getTransactionStats = () => {
        const total = transactions.length;
        const completed = transactions.filter(t => t.status === 'completed').length;
        const pending = transactions.filter(t => t.status === 'pending').length;
        const totalAmount = transactions
            .filter(t => t.status === 'completed')
            .reduce((sum, t) => sum + (t.type === 'credit' ? t.amount : -t.amount), 0);

        return { total, completed, pending, totalAmount };
    };

    const stats = getTransactionStats();

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                            <i className="ri-exchange-line text-blue-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">Total</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{stats.total}</p>
                        <p className="text-sm text-gray-600">Transactions</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-50 rounded-xl group-hover:bg-green-100 transition-colors">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Completed</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-green-600 mb-1">{stats.completed}</p>
                        <p className="text-sm text-gray-600">Successful</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-yellow-50 rounded-xl group-hover:bg-yellow-100 transition-colors">
                            <i className="ri-time-line text-yellow-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">Pending</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-yellow-600 mb-1">{stats.pending}</p>
                        <p className="text-sm text-gray-600">Awaiting</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                            <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">Volume</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">
                            {formatCurrency(stats.totalAmount)}
                        </p>
                        <p className="text-sm text-gray-600">Net Amount</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="p-8 border-b border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Transaction History</h3>
                            <p className="text-gray-600">View and manage all your payment transactions</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <i className="ri-search-line text-gray-400"></i>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search transactions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 w-full sm:w-64"
                                />
                            </div>
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            >
                                <option value="all">All Status</option>
                                <option value="completed">Completed</option>
                                <option value="pending">Pending</option>
                                <option value="failed">Failed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                            <span className="text-lg text-gray-600">Loading transactions...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('reference')}>
                                            <div className="flex items-center">
                                                Reference
                                                {sortBy === 'reference' && (
                                                    <i className={`ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-line ml-2 text-gray-400`}></i>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('amount')}>
                                            <div className="flex items-center">
                                                Amount
                                                {sortBy === 'amount' && (
                                                    <i className={`ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-line ml-2 text-gray-400`}></i>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                        <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                        <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                                        <th className="px-8 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('timestamp')}>
                                            <div className="flex items-center">
                                                Date
                                                {sortBy === 'timestamp' && (
                                                    <i className={`ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-line ml-2 text-gray-400`}></i>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedTransactions.map((transaction) => (
                                        <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-8 py-6 whitespace-nowrap">
                                                <div className="text-sm font-semibold text-gray-900">{transaction.reference}</div>
                                            </td>
                                            <td className="px-8 py-6 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className={`p-2 rounded-lg mr-3 ${
                                                        transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                                                    }`}>
                                                        <i className={`ri-${transaction.type === 'credit' ? 'arrow-up' : 'arrow-down'}-line ${
                                                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                                        }`}></i>
                                                    </div>
                                                    <span className="text-lg font-bold text-gray-900">
                                                        {formatCurrency(transaction.amount, transaction.currency)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 whitespace-nowrap">
                                                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                                                    transaction.type === 'credit' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {transaction.type}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6 whitespace-nowrap">
                                                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                                                    transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {transaction.status}
                                                </span>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="text-sm text-gray-900 max-w-xs truncate">
                                                    {transaction.description}
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 whitespace-nowrap text-sm text-gray-600">
                                                {formatDate(transaction.timestamp)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="text-sm text-gray-700">
                                        Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedTransactions.length)} of {sortedTransactions.length} results
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all duration-200 font-medium"
                                        >
                                            <i className="ri-arrow-left-line mr-1"></i>
                                            Previous
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-4 py-2 border rounded-lg text-sm transition-all duration-200 font-medium ${
                                                    currentPage === page
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all duration-200 font-medium"
                                        >
                                            Next
                                            <i className="ri-arrow-right-line ml-1"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BillingTransactions; 